import type { LessonVisual } from "@shared/lessonBlocks";
import { RichText } from "./RichText";

/** Small, dependency-free visuals used inside explain blocks and by the fraction / number line checks. */

export function FractionShape({
  parts,
  shaded,
  shape = "bar",
  onToggle,
  size = 220,
  className = "",
}: {
  parts: number;
  shaded: number | boolean[];
  shape?: "bar" | "circle" | "grid";
  onToggle?: (index: number) => void;
  size?: number;
  className?: string;
}) {
  const isShaded = (i: number) => (Array.isArray(shaded) ? !!shaded[i] : i < shaded);
  const fill = "hsl(var(--primary))";
  const empty = "hsl(var(--muted))";
  const stroke = "hsl(var(--foreground) / 0.6)";
  const interactive = !!onToggle;

  if (shape === "circle") {
    const r = size / 2 - 4;
    const cx = size / 2;
    const cy = size / 2;
    const slices = Array.from({ length: parts }, (_, i) => {
      const a0 = (i / parts) * Math.PI * 2 - Math.PI / 2;
      const a1 = ((i + 1) / parts) * Math.PI * 2 - Math.PI / 2;
      const x0 = cx + r * Math.cos(a0);
      const y0 = cy + r * Math.sin(a0);
      const x1 = cx + r * Math.cos(a1);
      const y1 = cy + r * Math.sin(a1);
      const large = 1 / parts > 0.5 ? 1 : 0;
      const d = parts === 1
        ? `M ${cx} ${cy} m -${r} 0 a ${r} ${r} 0 1 0 ${r * 2} 0 a ${r} ${r} 0 1 0 -${r * 2} 0`
        : `M ${cx} ${cy} L ${x0} ${y0} A ${r} ${r} 0 ${large} 1 ${x1} ${y1} Z`;
      return (
        <path
          key={i}
          d={d}
          fill={isShaded(i) ? fill : empty}
          stroke={stroke}
          strokeWidth={2}
          className={interactive ? "cursor-pointer transition-colors" : ""}
          onClick={() => onToggle?.(i)}
        />
      );
    });
    return (
      <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} className={`mx-auto ${className}`} role="img" aria-label={`${Array.isArray(shaded) ? shaded.filter(Boolean).length : shaded} of ${parts} parts shaded`}>
        {slices}
      </svg>
    );
  }

  if (shape === "grid") {
    const cols = Math.ceil(Math.sqrt(parts));
    const rows = Math.ceil(parts / cols);
    const cell = Math.floor(Math.min(size / cols, 64));
    return (
      <div className={`mx-auto grid gap-1 ${className}`} style={{ gridTemplateColumns: `repeat(${cols}, ${cell}px)`, width: cols * cell + (cols - 1) * 4 }} role="img">
        {Array.from({ length: rows * cols }, (_, i) => (
          i < parts ? (
            <button
              key={i}
              type="button"
              disabled={!interactive}
              onClick={() => onToggle?.(i)}
              className={`rounded-md border-2 transition-colors ${isShaded(i) ? "bg-primary border-primary/70" : "bg-muted border-border"} ${interactive ? "hover:opacity-80 cursor-pointer" : "cursor-default"}`}
              style={{ width: cell, height: cell }}
              aria-pressed={isShaded(i)}
            />
          ) : <div key={i} style={{ width: cell, height: cell }} />
        ))}
      </div>
    );
  }

  // bar (wide enough for small fingers; scales down with the container on phones)
  const w = Math.max(size * 1.4, 480);
  const h = 72;
  const cellW = w / parts;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" style={{ maxWidth: w }} className={`mx-auto block ${className}`} role="img" aria-label={`${Array.isArray(shaded) ? shaded.filter(Boolean).length : shaded} of ${parts} parts shaded`}>
      {Array.from({ length: parts }, (_, i) => (
        <rect
          key={i}
          x={i * cellW + 1}
          y={2}
          width={cellW - 2}
          height={h - 4}
          rx={6}
          fill={isShaded(i) ? fill : empty}
          stroke={stroke}
          strokeWidth={2}
          className={interactive ? "cursor-pointer transition-colors" : ""}
          onClick={() => onToggle?.(i)}
        />
      ))}
    </svg>
  );
}

export function NumberLineSvg({
  min,
  max,
  step,
  labelEvery,
  marks,
  value,
  onChange,
  className = "",
}: {
  min: number;
  max: number;
  step: number;
  labelEvery?: number;
  marks?: Array<{ value: number; label?: string }>;
  value?: number | null;
  onChange?: (v: number) => void;
  className?: string;
}) {
  const width = 640;
  const height = 96;
  const padX = 28;
  const lineY = 48;
  const span = max - min || 1;
  const xOf = (v: number) => padX + ((v - min) / span) * (width - padX * 2);
  const ticks: number[] = [];
  const stepsCount = Math.round(span / step);
  const tickEvery = stepsCount > 40 ? Math.ceil(stepsCount / 40) : 1;
  for (let i = 0; i <= stepsCount; i += tickEvery) ticks.push(min + i * step);
  const autoLabel = labelEvery ?? (() => {
    const candidates = [1, 2, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000, 2000, 2500, 5000];
    for (const c of candidates) if (span / c <= 10) return c;
    return span / 5;
  })();
  const labelTicks: number[] = [];
  for (let v = min; v <= max + 1e-9; v += autoLabel) labelTicks.push(Number(v.toFixed(6)));

  const fmt = (v: number) => {
    const s = Number.isInteger(v) ? String(v) : v.toFixed(2).replace(/\.?0+$/, "");
    return s.length > 3 && Number.isInteger(v) ? s.replace(/\B(?=(\d{3})+(?!\d))/g, " ") : s;
  };

  const snap = (clientX: number, svg: SVGSVGElement) => {
    const rect = svg.getBoundingClientRect();
    const rel = ((clientX - rect.left) / rect.width) * width;
    const raw = min + ((rel - padX) / (width - padX * 2)) * span;
    const snapped = Math.round((raw - min) / step) * step + min;
    return Math.max(min, Math.min(max, Number(snapped.toFixed(6))));
  };

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      className={`select-none touch-none ${onChange ? "cursor-pointer" : ""} ${className}`}
      onPointerDown={onChange ? (e) => { (e.currentTarget as SVGSVGElement).setPointerCapture(e.pointerId); onChange(snap(e.clientX, e.currentTarget)); } : undefined}
      onPointerMove={onChange ? (e) => { if (e.buttons & 1) onChange(snap(e.clientX, e.currentTarget)); } : undefined}
      role={onChange ? "slider" : "img"}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={value ?? undefined}
    >
      <line x1={padX - 10} y1={lineY} x2={width - padX + 10} y2={lineY} stroke="hsl(var(--foreground) / 0.7)" strokeWidth={3} strokeLinecap="round" />
      {ticks.map(t => {
        const major = labelTicks.some(l => Math.abs(l - t) < 1e-6);
        return <line key={t} x1={xOf(t)} y1={lineY - (major ? 12 : 6)} x2={xOf(t)} y2={lineY + (major ? 12 : 6)} stroke="hsl(var(--foreground) / 0.6)" strokeWidth={major ? 2.5 : 1.5} />;
      })}
      {labelTicks.map(t => (
        <text key={`l${t}`} x={xOf(t)} y={lineY + 30} textAnchor="middle" fontSize={14} fill="hsl(var(--foreground))" fontWeight={600}>{fmt(t)}</text>
      ))}
      {marks?.map((m, i) => (
        <g key={`m${i}`}>
          <circle cx={xOf(m.value)} cy={lineY} r={7} fill="hsl(var(--chart-2))" stroke="white" strokeWidth={2} />
          {m.label && <text x={xOf(m.value)} y={lineY - 18} textAnchor="middle" fontSize={13} fill="hsl(var(--chart-2))" fontWeight={700}>{m.label}</text>}
        </g>
      ))}
      {typeof value === "number" && (
        <g>
          <line x1={xOf(value)} y1={lineY - 22} x2={xOf(value)} y2={lineY + 16} stroke="hsl(var(--primary))" strokeWidth={4} strokeLinecap="round" />
          <circle cx={xOf(value)} cy={lineY - 26} r={11} fill="hsl(var(--primary))" stroke="white" strokeWidth={3} />
          <text x={xOf(value)} y={lineY - 40} textAnchor="middle" fontSize={15} fill="hsl(var(--primary))" fontWeight={800}>{fmt(value)}</text>
        </g>
      )}
    </svg>
  );
}

export function LessonVisualView({ visual }: { visual: LessonVisual }) {
  switch (visual.kind) {
    case "table":
      return (
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-sm md:text-base">
            <thead className="bg-primary/10">
              <tr>
                {visual.headers.map((h, i) => <th key={i} className="px-3 py-2 text-left font-bold whitespace-nowrap"><RichText text={h} inline /></th>)}
              </tr>
            </thead>
            <tbody>
              {visual.rows.map((row, r) => (
                <tr key={r} className={r % 2 ? "bg-muted/40" : ""}>
                  {row.map((cell, c) => <td key={c} className="px-3 py-2 whitespace-nowrap"><RichText text={cell} inline /></td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case "emojiGrid":
      return (
        <div className="text-center">
          <div className="inline-block rounded-2xl bg-muted/50 px-5 py-3 text-3xl md:text-4xl leading-snug tracking-wider">
            {visual.rows.map((row, i) => <div key={i}>{row}</div>)}
          </div>
          {visual.caption && <p className="text-sm text-muted-foreground mt-2">{visual.caption}</p>}
        </div>
      );
    case "fraction":
      return (
        <div className="text-center">
          <FractionShape parts={visual.parts} shaded={visual.shaded} shape={visual.shape} />
          {visual.caption && <p className="text-sm text-muted-foreground mt-2">{visual.caption}</p>}
        </div>
      );
    case "numberLine":
      return (
        <div className="text-center">
          <NumberLineSvg min={visual.min} max={visual.max} step={visual.step} marks={visual.marks} />
          {visual.caption && <p className="text-sm text-muted-foreground mt-1">{visual.caption}</p>}
        </div>
      );
    default:
      return null;
  }
}
